import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { getEmbedUrl } from '../utils/videoUtils';

function VideoCard({ video, onPlay, isActive }) {
  const isReel = video.type === 'reel';

  return (
    <div className={`group relative w-full rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 cursor-pointer bg-black ${isReel ? 'aspect-[9/16] max-w-[320px] mx-auto' : 'aspect-video'}`}>
      {!isActive ? (
        <>
          <img 
            src={video.thumbnail} 
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 group-hover:scale-110 transition-all duration-500 border border-white/30">
              <svg className="w-6 h-6 text-white fill-current translate-x-0.5" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
            <h3 className="text-[10px] tracking-[0.3em] font-black uppercase text-white">
              {video.title}
            </h3>
          </div>
        </>
      ) : (
        <>
          <iframe
            title={video.title}
            src={video.embed}
            className="absolute inset-0 w-full h-full border-0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onPlay(null);
            }}
            className="absolute top-4 right-4 bg-black/70 hover:bg-black text-white text-[8px] px-3 py-1.5 rounded-full uppercase tracking-[0.2em] font-bold transition-colors z-20 backdrop-blur-sm shadow-lg border border-white/20"
          >
            ✕ {video.type === 'reel' ? '' : 'Zatvori'}
          </button>
        </>
      )}
    </div>
  );
}

export default function FeaturedVideos({ settings }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'bs';
  const [videos, setVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filmsTitle = currentLang === 'en' 
    ? (settings?.filmsTitleEn || 'Films')
    : (settings?.filmsTitleBs || 'Filmovi');
  
  const filmsDescription = currentLang === 'en'
    ? (settings?.filmsDescriptionEn || 'See what our work looks like - cinematic stories that last forever.')
    : (settings?.filmsDescriptionBs || 'Pogledajte kako naš rad izgleda - činematografske priče koje ostaju zauvijek.');

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const q = query(collection(db, 'films'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        
        const allItems = [];
        snap.docs.forEach(d => {
          const data = d.data();
          const url = (data.url || '').trim();
          const embed = getEmbedUrl(url);

          if (embed) {
            allItems.push({
              ...data,
              id: d.id,
              embed,
              thumbnail: data.thumbnail || '/img/photos/default.jpg'
            });
          }
        });

        const featured = allItems.filter(v => v.showOnLanding === true);
        
        // Ako nema odabranih za landing, uzmi zadnja 2 kao fallback
        if (featured.length > 0) {
          setVideos(featured);
        } else {
          setVideos(allItems.slice(0, 2));
        }
      } catch (err) {
        console.error('Error loading videos:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadVideos();
  }, []);

  return (
    <section id="videos" className="py-24 md:py-32 px-6 md:px-12 bg-white font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 space-y-6">
          <div>
            <h2 className="text-5xl md:text-6xl font-serif text-gray-900 leading-tight tracking-wide mb-4">
              {filmsTitle}
            </h2>
          </div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            {filmsDescription}
          </p>
        </div>

        {/* Videos Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{i18n.language === 'bs' ? 'Učitavanje...' : 'Loading...'}</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400">{i18n.language === 'bs' ? 'Greška:' : 'Error:'} {error}</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{i18n.language === 'bs' ? 'Nema dostupnih videa' : 'No videos available'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
            {videos.map((video) => (
              <div key={video.id} onClick={() => setActiveVideo(video.id)}>
                <VideoCard 
                  video={video}
                  onPlay={setActiveVideo}
                  isActive={activeVideo === video.id}
                />
              </div>
            ))}
          </div>
        )}

        {/* YouTube Channel Link */}
        {settings?.youtubeChannelUrl && (
          <div className="mt-16 text-center">
            <a 
              href={settings.youtubeChannelUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-black text-white rounded-full font-black text-[11px] uppercase tracking-[0.3em] hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl"
            >
              {i18n.language === 'bs' ? 'Više filmova na YouTube kanalu →' : 'More films on YouTube channel →'}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
